import React, { memo, useCallback } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import type { DailyShiftSlotGrouped, TimeSlot, IncentiveSettingInput } from '../../../../domain/dailyShiftSlot';
import type { UseMonthlyShiftSlotSetupReturn } from '../hooks/useMonthlyShiftSlotSetup';
import ShiftSlotCalendar from '../components/ShiftSlotCalendar';
import DateDetailModal from '../components/DateDetailModal';
import DoctorSelectDialog from '../components/DoctorSelectDialog';
import ImportDialog from '../components/ImportDialog';
import DateTimePickerDialog from '../components/DateTimePickerDialog';
import UrgentSettingsModal from '../components/UrgentSettingsModal';
import CopyDailySlotsDialog from '../components/CopyDailySlotsDialog';
import UrgentTargetListModal from '../components/UrgentTargetListModal';
import UrgentTargetDetailDialog from '../components/UrgentTargetDetailDialog';
import { ErrorContainer, LoadingOverlay } from '../MonthlyShiftSlotSetup.styles';

interface MonthlyShiftSlotSetupContainerProps {
  hookState: UseMonthlyShiftSlotSetupReturn;
}

/**
 * 月次シフト枠設定コンテナコンポーネント
 *
 * - Hook状態をpropsで受け取る
 * - カレンダーとダイアログの組み立て
 * - エラー表示
 * - ローディング状態管理
 */
const MonthlyShiftSlotSetupContainer: React.FC<MonthlyShiftSlotSetupContainerProps> = memo(({
  hookState,
}) => {
  const {
    data,
    isLoading,
    error,
    currentYear,
    currentMonth,
    editedSlots,
    hasUnsavedChanges,
    selectedDate,
    dateDetailModalOpen,
    doctorSelectDialogOpen,
    doctorSelectDialogType,
    importDialogOpen,
    dateTimePickerDialogOpen,
    dateTimePickerDialogType,
    urgentSettingsModalOpen,
    copyDialogOpen,
    urgentTargets,
    urgentTargetsLoading,
    urgentTargetListModalOpen,
    selectedUrgentTarget,
    urgentTargetDetailDialogOpen,
    // データ操作
    saveChanges,
    // 枠編集
    updateSlot,
    // コピー
    copySlotsToTargetDates,
    // ステータス操作
    startRecruitment,
    closeRecruitment,
    // 通知送信
    sendVacancyNotificationToDoctor,
    sendUrgentNotificationToDoctor,
    // インポート
    handleImport,
    // モーダル操作
    openDateDetailModal,
    closeDateDetailModal,
    closeDoctorSelectDialog,
    closeImportDialog,
    closeDateTimePickerDialog,
    openUrgentSettingsModal,
    closeUrgentSettingsModal,
    openCopyDialog,
    closeCopyDialog,
    // 急募一覧・詳細
    closeUrgentTargetListModal,
    openUrgentTargetDetail,
    closeUrgentTargetDetail,
    confirmDoctorDirectly,
    cancelUrgentTarget,
    cancelApplication,
    // 急募設定保存
    saveIncentiveSettings,
    saveSelectedUrgentTargetSettings,
    // ユーティリティ
    clearError,
  } = hookState;

  const handleDateTimeConfirm = async (datetime: string): Promise<boolean> => {
    if (dateTimePickerDialogType === 'recruitmentStart') {
      return await startRecruitment(datetime);
    } else if (dateTimePickerDialogType === 'deadline') {
      return await closeRecruitment(datetime);
    }
    return false;
  };

  const handleDoctorSelectSubmit = async (doctorIds: string[]): Promise<boolean> => {
    if (doctorSelectDialogType === 'vacancy') {
      return await sendVacancyNotificationToDoctor(doctorIds);
    } else if (doctorSelectDialogType === 'urgent') {
      return await sendUrgentNotificationToDoctor(doctorIds);
    }
    return false;
  };

  /**
   * インセンティブ設定保存ハンドラー
   * 急募設定保存APIを呼び出し、成功時のみモーダルを閉じる
   * （失敗時はモーダルを開いたまま再入力可能とする）
   */
  const handleSaveIncentiveSettings = useCallback(async (settings: IncentiveSettingInput) => {
    const success = await saveIncentiveSettings(settings);
    if (success) {
      closeUrgentSettingsModal();
    }
  }, [saveIncentiveSettings, closeUrgentSettingsModal]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* ローディングオーバーレイ */}
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      {/* エラー表示 */}
      {error && (
        <ErrorContainer>
          <Alert severity="error" onClose={clearError}>
            {error}
          </Alert>
        </ErrorContainer>
      )}

      {/* カレンダー（ヘッダーなし） */}
      <ShiftSlotCalendar
        year={currentYear}
        month={currentMonth}
        days={data?.days || []}
        onDayClick={openDateDetailModal}
        disabled={isLoading}
        showHeader={false}
      />

      {/* 日付詳細モーダル */}
      <DateDetailModal
        open={dateDetailModalOpen}
        date={selectedDate}
        editedSlots={editedSlots}
        onSlotChange={updateSlot}
        onClose={closeDateDetailModal}
        onSave={saveChanges}
        onOpenCopyDialog={openCopyDialog}
        hasUnsavedChanges={hasUnsavedChanges}
        isLoading={isLoading}
      />

      {/* 日毎シフト枠コピーダイアログ */}
      <CopyDailySlotsDialog
        open={copyDialogOpen}
        sourceDate={selectedDate}
        days={data?.days || []}
        onClose={closeCopyDialog}
        onCopy={copySlotsToTargetDates}
        isLoading={isLoading}
      />

      {/* 医師選択ダイアログ */}
      <DoctorSelectDialog
        open={doctorSelectDialogOpen}
        type={doctorSelectDialogType}
        onClose={closeDoctorSelectDialog}
        onSubmit={handleDoctorSelectSubmit}
        isLoading={isLoading}
      />

      {/* インポートダイアログ */}
      <ImportDialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        onImport={handleImport}
        isLoading={isLoading}
      />

      {/* 日時選択ダイアログ */}
      <DateTimePickerDialog
        open={dateTimePickerDialogOpen}
        type={dateTimePickerDialogType}
        onClose={closeDateTimePickerDialog}
        onConfirm={handleDateTimeConfirm}
        isLoading={isLoading}
      />

      {/* 急募一覧モーダル */}
      <UrgentTargetListModal
        open={urgentTargetListModalOpen}
        targets={urgentTargets}
        isLoading={urgentTargetsLoading}
        onClose={closeUrgentTargetListModal}
        onOpenUrgentSettings={openUrgentSettingsModal}
        onOpenDetail={openUrgentTargetDetail}
      />

      {/* 急募詳細ダイアログ */}
      <UrgentTargetDetailDialog
        open={urgentTargetDetailDialogOpen}
        target={selectedUrgentTarget}
        deadlineAt={data?.deadlineAt ?? null}
        isLoading={isLoading}
        error={error}
        onClearError={clearError}
        onConfirmDoctor={confirmDoctorDirectly}
        onCancelUrgent={cancelUrgentTarget}
        onCancelApplication={cancelApplication}
        onSaveUrgentSettings={saveSelectedUrgentTargetSettings}
        onClose={closeUrgentTargetDetail}
      />

      {/* 急募設定モーダル */}
      <UrgentSettingsModal
        open={urgentSettingsModalOpen}
        slots={data?.days || []}
        year={currentYear}
        month={currentMonth}
        onSave={handleSaveIncentiveSettings}
        onClose={closeUrgentSettingsModal}
      />
    </Box>
  );
});

MonthlyShiftSlotSetupContainer.displayName = 'MonthlyShiftSlotSetupContainer';

export default MonthlyShiftSlotSetupContainer;
